import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {Object} ContainedBackgroundCtaRefs
 * @property {HTMLElement[]} slides
 * @property {HTMLButtonElement[]} [dots]
 */

const AUTOPLAY_MS = 5000;

/** Horizontal drag distance that counts as a swipe. */
const SWIPE_THRESHOLD_PX = 40;

/** Window after a swipe in which the trailing click is swallowed. */
const SWIPE_CLICK_SUPPRESS_MS = 400;

/**
 * Fades between stacked callout slides with clickable pagination.
 *
 * @extends {Component<ContainedBackgroundCtaRefs>}
 */
class ContainedBackgroundCta extends Component {
  requiredRefs = ['slides'];

  /** @type {number} */
  #index = 0;

  /** @type {ReturnType<typeof setInterval> | null} */
  #timer = null;

  /** @type {AbortController | null} */
  #abort = null;

  /** @type {{ x: number, y: number, id: number } | null} */
  #swipeStart = null;

  /** @type {number} */
  #swipedAt = 0;

  connectedCallback() {
    super.connectedCallback();

    const { slides } = this.refs;
    if (!slides?.length) return;

    this.#index = Math.max(
      0,
      slides.findIndex((slide) => slide.classList.contains('is-active'))
    );

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('mouseenter', this.#pause, { signal });
    this.addEventListener('mouseleave', this.#resume, { signal });
    this.addEventListener('focusin', this.#pause, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });

    document.addEventListener('visibilitychange', this.#onVisibilityChange, { signal });

    if (slides.length > 1) {
      // Swipe between slides: a horizontal drag past the threshold moves the
      // slideshow, vertical movement is left to the page scroll (touch-action:
      // pan-y on the panel), and the click that follows a swipe is swallowed so
      // a button under the finger doesn't fire.
      this.addEventListener('pointerdown', this.#onPointerDown, { signal });
      this.addEventListener('pointerup', this.#onPointerUp, { signal });
      this.addEventListener('pointercancel', this.#onPointerCancel, { signal });
      this.addEventListener('click', this.#onClickAfterSwipe, { capture: true, signal });
    }

    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    this.#abort?.abort();
    this.#abort = null;
  }

  /**
   * Select a slide by zero-based index (used by pagination `on:click`).
   * @param {number | string} index
   * @param {Event} [_event]
   */
  select(index, _event) {
    const next = Number(index);
    if (!Number.isFinite(next)) return;

    this.#goTo(next);
    this.#restart();
  }

  #start() {
    if (prefersReducedMotion()) return;
    if ((this.refs.slides?.length ?? 0) <= 1) return;

    this.#stop();
    this.#timer = setInterval(() => {
      this.#goTo(this.#index + 1);
    }, AUTOPLAY_MS);
  }

  #stop() {
    if (this.#timer == null) return;
    clearInterval(this.#timer);
    this.#timer = null;
  }

  #restart() {
    this.#stop();
    this.#start();
  }

  #pause = () => {
    this.#stop();
  };

  #resume = () => {
    this.#start();
  };

  #onFocusOut = () => {
    requestAnimationFrame(() => {
      if (!this.contains(document.activeElement)) {
        this.#start();
      }
    });
  };

  #onVisibilityChange = () => {
    if (document.hidden) {
      this.#stop();
    } else {
      this.#start();
    }
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.#swipeStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    const start = this.#swipeStart;
    if (!start || event.pointerId !== start.id) return;
    this.#swipeStart = null;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) < Math.abs(dy)) return;

    this.#swipedAt = Date.now();
    this.#goTo(this.#index + (dx < 0 ? 1 : -1));
    this.#restart();
  };

  #onPointerCancel = () => {
    this.#swipeStart = null;
  };

  /** @param {MouseEvent} event */
  #onClickAfterSwipe = (event) => {
    if (Date.now() - this.#swipedAt >= SWIPE_CLICK_SUPPRESS_MS) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  /**
   * @param {number} next
   */
  #goTo(next) {
    const { slides, dots } = this.refs;
    if (!slides?.length) return;

    const length = slides.length;
    const index = ((next % length) + length) % length;
    if (index === this.#index && slides[index]?.classList.contains('is-active')) return;

    this.#index = index;

    for (const [i, slide] of slides.entries()) {
      const active = i === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');

      if (active) {
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('inert', '');
      }
    }

    if (dots?.length) {
      for (const [i, dot] of dots.entries()) {
        const active = i === index;
        dot.classList.toggle('is-active', active);

        if (active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      }
    }
  }
}

if (!customElements.get('contained-background-cta')) {
  customElements.define('contained-background-cta', ContainedBackgroundCta);
}
